// @flow
import React, { type Node } from 'react';
import cn from 'classnames';

import OveflowedText from 'app/common/components/OveflowedText';
import PlainIcon, { iconTypes, colorTypes } from 'app/common/ui-next/plain-icon';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';

import QuantityControl from './QuantityControl';
import styles from './OrderItem.module.scss';;


type Props = {
  className?: string,
  title: string,
  portion: string,
  cost: number,
  type: 0 | 1 | 'item' | 'complex',
  n: number,
  guid: string,
  incDisabled: boolean,
  locked?: boolean,
  currency: string,
  onCountIncrement: (id: string) => void,
  onCountDecrement: (id: string) => void,
  onRemove: (id: string) => void,
}

const OrderItem = ({
  className,
  title,
  portion,
  cost,
  type,
  n,
  guid,
  incDisabled,
  locked,
  currency,
  onCountIncrement,
  onCountDecrement,
  onRemove,
}: Props): Node => {
  const handleInc = React.useCallback((e) => {
    e.preventDefault();
    if (locked) return;
    onCountIncrement(guid);
  }, [onCountIncrement, guid, locked]);

  const handleDec = React.useCallback((e) => {
    e.preventDefault();
    if (locked) return;
    onCountDecrement(guid);
  }, [onCountDecrement, guid, locked]);

  const handleRemove = React.useCallback((e) => {
    e.preventDefault();
    if (locked) return;
    onRemove(guid);
  }, [onRemove, guid, locked]);


  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.icon}>
        {(type === 0 || type === 'item')
          && <PlainIcon
            width="40px"
            height="auto"
            type={iconTypes.DISH}
            colorType={colorTypes.COLOR_PRIMARY}
          />}
        {(type === 1 || type === 'complex')
          && <PlainIcon
            width="38px"
            height="auto"
            type={iconTypes.COMPLEX}
            colorType={colorTypes.COLOR_PRIMARY}
          />}
      </div>
      <div className={styles.main}>
        <div className={styles.title}>
          <OveflowedText>
            {title}
          </OveflowedText>
        </div>
        <div className={styles.info}>
          <div className={styles.portion}>
            {portion
              && <PlainIcon
                type={iconTypes.PORTION}
                colorType={colorTypes.COLOR_DARKGRAY}
              />}
            &nbsp;
            {portion}
          </div>
        </div>
      </div>
      <div className={styles.quantity}>
        <QuantityControl
          className={styles.quantityItem}
          n={n}
          incDisabled={incDisabled}
          onClickPlus={handleInc}
          onClickMinus={handleDec}
          onClickCross={handleRemove}
        />
        <div className={cn(styles.quantityItem, styles.cost)}>
          {toFixedValueCurrency(cost, false, currency)}
        </div>
      </div>
      {locked && <div className={styles.locked} />}
    </div>
  );
};

export default OrderItem;
